import { useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { MathText } from "./MathText";

interface QuestionPreviewProps {
  questionText: string;
  questionType: string;
  options: Record<string, string> | null;
  correctAnswer: string | null;
  explanation: string | null;
  imageUrl: string | null;
  passageContent: string | null;
  subject: string;
  topic: string;
  /** "preview" shows unanswered, "correct" shows right answer, "wrong" shows wrong */
  mode: "preview" | "correct" | "wrong";
}

/**
 * Student-view simulation of a question.
 *
 * Matches the student app's QuestionScreen layout so workers can see
 * exactly what the student will see — including passage, image, options,
 * and the feedback state after answering.
 */
export function QuestionPreview({
  questionText,
  questionType,
  options,
  correctAnswer,
  explanation,
  imageUrl,
  passageContent,
  subject,
  topic,
  mode,
}: QuestionPreviewProps) {
  const [collapsed, setCollapsed] = useState(false);
  const isObjective = options && Object.keys(options).length > 0;

  // Simulate a wrong answer: pick the first option that isn't the correct one
  const wrongAnswer =
    isObjective && correctAnswer
      ? (Object.keys(options).find((k) => k !== correctAnswer) ?? null)
      : null;
  const chosenAnswer = mode === "correct" ? correctAnswer : mode === "wrong" ? wrongAnswer : null;
  const answered = mode !== "preview";

  return (
    <div className="rounded-xl border border-border bg-[#0A0F1E] text-[#E8E8E4]">
      {/* Header — matches student app */}
      <div className="flex items-center justify-between border-b border-[#1E2336] px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-[#8B8FA3]">{subject || "Subject"}</span>
          {topic ? (
            <>
              <span className="text-[10px] text-[#8B8FA3]">·</span>
              <span className="text-[12px] text-[#8B8FA3]">{topic}</span>
            </>
          ) : null}
        </div>
        <span className="text-[12px] text-[#8B8FA3]">Q1 of 10</span>
      </div>

      <div className="px-5 py-5">
        {/* Passage */}
        {passageContent ? (
          <div className="mb-4 rounded-lg border border-[#1E2336] bg-[#111827] p-4">
            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[#8B8FA3]"
            >
              <ChevronDown
                size={13}
                aria-hidden="true"
                className={`transition-transform ${collapsed ? "-rotate-90" : ""}`}
              />
              Passage
            </button>
            {!collapsed ? (
              <p className="text-[13.5px] leading-[1.65] text-[#C8C8C4]">{passageContent}</p>
            ) : null}
          </div>
        ) : null}

        {/* Image */}
        {imageUrl ? (
          <div className="mb-4 overflow-hidden rounded-lg border border-[#1E2336]">
            <img
              src={imageUrl}
              alt="Question diagram"
              className="max-h-64 w-full object-contain bg-[#111827] p-2"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
                (e.target as HTMLImageElement).insertAdjacentHTML(
                  "afterend",
                  '<p class="p-4 text-center text-[13px] text-[#8B8FA3]">Image could not load</p>',
                );
              }}
            />
          </div>
        ) : null}

        {/* Question stem */}
        <h2 className="text-[17px] font-semibold leading-[1.45]">
          {questionText ? <MathText text={questionText} /> : "Question text will appear here…"}
        </h2>

        {/* MCQ Options */}
        {isObjective ? (
          <div className="mt-5 flex flex-col gap-2.5">
            {Object.entries(options).map(([key, text]) => {
              let state: "idle" | "selected" | "correct" | "wrong" = "idle";
              if (answered) {
                if (key === correctAnswer) state = "correct";
                else if (key === chosenAnswer) state = "wrong";
              } else if (key === chosenAnswer) {
                state = "selected";
              }

              const colors = {
                idle: "border-[#1E2336] bg-[#111827]",
                selected: "border-[#6366F1] bg-[#6366F1]/10",
                correct: "border-[#22C55E] bg-[#22C55E]/10",
                wrong: "border-[#EF4444] bg-[#EF4444]/10",
              };

              const letterColors = {
                idle: "bg-[#1E2336] text-[#8B8FA3]",
                selected: "bg-[#6366F1]/20 text-[#818CF8]",
                correct: "bg-[#22C55E]/20 text-[#22C55E]",
                wrong: "bg-[#EF4444]/20 text-[#EF4444]",
              };

              return (
                <div
                  key={key}
                  className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${colors[state]}`}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[13px] font-bold ${letterColors[state]}`}
                  >
                    {key}
                  </span>
                  <span className="text-[14px]">
                    {text ? <MathText text={text} /> : `Option ${key}`}
                  </span>
                  {state === "correct" ? (
                    <Check size={15} aria-hidden="true" className="ml-auto text-[#22C55E]" />
                  ) : state === "wrong" ? (
                    <X size={15} aria-hidden="true" className="ml-auto text-[#EF4444]" />
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        {/* Essay textarea (theory) */}
        {questionType === "essay" ? (
          <div className="mt-5">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#8B8FA3]">
              Your answer
            </label>
            <div className="mt-2 rounded-lg border border-[#1E2336] bg-[#111827] p-3.5 text-[14.5px] leading-[1.55] text-[#8B8FA3]">
              {answered
                ? "The student's written answer would appear here…"
                : "Write your answer as you would in the exam…"}
            </div>
          </div>
        ) : null}

        {/* Explanation (after answering) */}
        {answered && explanation ? (
          <div className="mt-5 rounded-lg border border-[#1E2336] bg-[#111827] p-4">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-[#8B8FA3]">
              Explanation
            </p>
            <p className="text-[13.5px] leading-[1.6] text-[#C8C8C4]">
              <MathText text={explanation} />
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
